import { auth } from '../firebase';
import { sendPasswordResetEmail, sendEmailVerification } from 'firebase/auth';
import { AthleteProfile } from '../types';

/**
 * Utility function to send password reset email via Firebase Auth.
 * Dispatches reset instructions to the specified email address.
 */
export async function sendPasswordResetEmailUtility(email: string): Promise<{ success: boolean; message: string }> {
  const cleanEmail = email.trim();
  if (!cleanEmail) {
    throw new Error('Email address is required to send password reset.');
  }

  try {
    await sendPasswordResetEmail(auth, cleanEmail);
    return {
      success: true,
      message: `Password reset link dispatched to ${cleanEmail} from support@playgroundleague.pro! Please check your inbox and spam folder.`,
    };
  } catch (err: any) {
    console.warn('sendPasswordResetEmail error:', err);
    let errorText = 'Unable to send password reset email.';
    if (err?.code === 'auth/user-not-found') {
      errorText = `No registered athlete account found for "${cleanEmail}". Please check spelling or register a new profile.`;
    } else if (err?.code === 'auth/invalid-email') {
      errorText = 'Please enter a valid email address.';
    } else if (err?.code === 'auth/unauthorized-domain') {
      errorText = `Domain "${window.location.hostname}" is not whitelisted in Firebase Authorized Domains.`;
    } else if (err?.message) {
      errorText = err.message;
    }
    throw new Error(errorText);
  }
}

/**
 * Centralized utility to send an email verification link and 6-digit OTP code.
 * Dispatches via Firebase Auth if active user is present, and dispatches via backend email API with 6-digit OTP.
 */
export async function sendVerificationEmailUtility(
  email: string,
  name?: string
): Promise<{ success: boolean; message: string; verificationCode?: string; verifyUrl?: string; isSmtpConfigured?: boolean }> {
  const cleanEmail = email.trim();
  if (!cleanEmail) {
    throw new Error('Email address is required to send verification email.');
  }

  let firebaseSent = false;
  let backendResult: any = null;

  // 1. Try Firebase Auth client verification
  if (auth.currentUser && auth.currentUser.email?.toLowerCase() === cleanEmail.toLowerCase()) {
    try {
      await sendEmailVerification(auth.currentUser);
      firebaseSent = true;
    } catch (err: any) {
      console.warn('Firebase Auth sendEmailVerification notice:', err);
    }
  }

  // 2. Call backend verification endpoint to dispatch SMTP and generate 6-digit code
  try {
    const res = await fetch('/api/email/verification-link', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: cleanEmail, name: name || 'Athlete' }),
    });
    if (res.ok) {
      backendResult = await res.json();
    }
  } catch (backendErr) {
    console.warn('Backend verification API call notice:', backendErr);
  }

  const code = backendResult?.verificationCode;
  const isSmtp = backendResult?.isSmtpConfigured;

  let msg = `Verification email dispatched to ${cleanEmail}! Please check your inbox and spam folder.`;
  if (code) {
    msg = `Verification email & security code sent to ${cleanEmail}!`;
  }

  return {
    success: true,
    message: msg,
    verificationCode: code,
    verifyUrl: backendResult?.verifyUrl,
    isSmtpConfigured: isSmtp,
  };
}

/**
 * Validates a 6-digit verification code against the backend.
 */
export async function verifyEmailCodeUtility(
  email: string,
  code: string
): Promise<{ success: boolean; message: string }> {
  const cleanEmail = email.trim();
  const cleanCode = code.trim();

  if (!cleanEmail || !cleanCode) {
    return { success: false, message: 'Email and 6-digit verification code are required.' };
  }

  try {
    const res = await fetch('/api/email/verify-code', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: cleanEmail, code: cleanCode }),
    });

    const data = await res.json();
    if (res.ok && data.success) {
      return { success: true, message: data.message || 'Email verified successfully!' };
    }
    return { success: false, message: data.error || 'Invalid or expired verification code.' };
  } catch (err: any) {
    console.error('Verify code error:', err);
    return { success: false, message: err?.message || 'Failed to verify code due to network error.' };
  }
}

/**
 * Centralized utility to verify if an athlete has an active PRO membership tier.
 * Returns true if the user has an active PRO flag, verified PRO flag, PRO tier, or PRO role.
 */
export function verifyProAccess(user: AthleteProfile | null | undefined): boolean {
  if (!user) return false;
  return Boolean(
    user.isPro === true ||
    user.isVerifiedPro === true ||
    user.subscriptionTier === 'pro' ||
    user.role === 'playground_pro'
  );
}

/**
 * Validates an email address against an allowed domain whitelist.
 * Returns true if allowedEmailDomains is empty or if the email's domain matches one of the whitelisted domains.
 */
export function validateEmailDomainWhitelist(email: string, allowedEmailDomains?: string[]): { isValid: boolean; domain: string; error?: string } {
  const cleanEmail = email.trim().toLowerCase();
  if (!cleanEmail || !cleanEmail.includes('@')) {
    return { isValid: false, domain: '', error: 'Please enter a valid email address.' };
  }

  const atIdx = cleanEmail.indexOf('@');
  const userDomain = cleanEmail.substring(atIdx); // e.g. "@college.edu"

  if (!allowedEmailDomains || allowedEmailDomains.length === 0) {
    return { isValid: true, domain: userDomain };
  }

  const cleanWhitelist = allowedEmailDomains
    .map(d => d.trim().toLowerCase())
    .filter(d => d.length > 0)
    .map(d => d.startsWith('@') ? d : `@${d}`);

  const isMatched = cleanWhitelist.includes(userDomain);

  if (!isMatched) {
    return {
      isValid: false,
      domain: userDomain,
      error: `Registration Restricted: Your email domain (${userDomain}) is not authorized. Whitelisted domains: ${cleanWhitelist.join(', ')}`,
    };
  }

  return { isValid: true, domain: userDomain };
}

